import { useEffect, useMemo, useState } from "react"
import { Head, router, usePage } from "@inertiajs/react"
import { DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core"
import { Ban, CalendarDays, ChevronLeft, ChevronRight, LayoutGrid, List, Rows3, X, type LucideIcon } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { CalendarWeekView } from "@/components/calendar/CalendarWeekView"
import { CalendarMonthView } from "@/components/calendar/CalendarMonthView"
import { CalendarListView } from "@/components/calendar/CalendarListView"
import { ChannelFilters } from "@/components/calendar/ChannelFilters"
import { TagFilter } from "@/components/calendar/TagFilter"
import { PiecePreviewProvider } from "@/components/calendar/PiecePreviewModal"
import { BulkPushBackModal } from "@/components/calendar/BulkPushBackModal"
import { AddBlockModal } from "@/components/calendar/AddBlockModal"
import { cn } from "@/lib/utils"
import type {
  CalendarSlot,
  CalendarChannel,
  CalendarFilterTag,
  CalendarHoliday,
  CalendarTemplateSlot,
  CalendarBlock,
  CalendarView
} from "@/components/calendar/types"

type Props = {
  view: CalendarView
  anchor: string
  window: { from: string; to: string }
  channels: CalendarChannel[]
  tags: CalendarFilterTag[]
  slots: CalendarSlot[]
  holidays: CalendarHoliday[]
  blocks: CalendarBlock[]
  template_slots: CalendarTemplateSlot[]
}

// Remembers the user's last-chosen calendar view in this browser.
const CALENDAR_VIEW_STORAGE_KEY = "sparkdrop:calendar-view"

function isCalendarView(value: string): value is CalendarView {
  return value === "week" || value === "month" || value === "list"
}

export default function Calendar({ view, anchor, channels, tags, slots, holidays, blocks, template_slots }: Props) {
  const { errors } = usePage().props as unknown as { errors?: Record<string, string> }
  // Server-side rejections (e.g. dragging a piece into a blocked window)
  // come back as the shared `errors` prop; surface them in a dismissible
  // banner since the calendar has no other error affordance.
  const [dismissedError, setDismissedError] = useState<string | null>(null)
  const errorMessage = errors && Object.values(errors)[0]

  const [visibleChannels, setVisibleChannels] = useState<Set<string>>(
    () => new Set(channels.map((c) => c.slug))
  )

  // Single-select tag filter (null = all tags). Options come from the
  // server's `tags` list — only tags on a piece scheduled today or later.
  // If the selected tag ages out of that list (e.g. its last piece
  // publishes), fall back to "all" so we never filter by a missing tag.
  const [selectedTagId, setSelectedTagId] = useState<number | null>(null)
  useEffect(() => {
    if (selectedTagId != null && !tags.some((t) => t.id === selectedTagId)) {
      setSelectedTagId(null)
    }
  }, [tags, selectedTagId])

  const visibleSlots = useMemo(
    () => (selectedTagId == null ? slots : slots.filter((s) => s.tags.some((t) => t.id === selectedTagId))),
    [slots, selectedTagId]
  )

  const toggleChannel = (slug: string) => {
    setVisibleChannels((prev) => {
      const next = new Set(prev)
      next.has(slug) ? next.delete(slug) : next.add(slug)
      return next
    })
  }

  const setAllChannels = (visible: boolean) => {
    setVisibleChannels(visible ? new Set(channels.map((c) => c.slug)) : new Set())
  }

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const onDragEnd = (event: DragEndEvent) => {
    // The browser fires a trailing `click` right after the drag's pointerup,
    // targeted at the dragged card's <Link>. Left alone, that click navigates
    // to the piece view (or opens the preview modal) the moment the drop
    // completes. Swallow it at the document level in the capture phase —
    // per-card suppression is unreliable because the PATCH response can
    // re-render the calendar (detaching/remounting the card) before the
    // click dispatches.
    suppressTrailingClick()

    const { active, over } = event
    if (!over) return

    const slot = active.data.current?.slot as CalendarSlot | undefined
    const data = over.data.current as { date: Date; hour: number } | undefined
    if (!slot || !data) return

    const returnTo = `${window.location.pathname}${window.location.search}`

    // Dropping onto today: let the server pick the earliest open slot still
    // ahead of now today, falling back to 9 AM today even if that's in the
    // past (the piece then shows a past-due warning to reschedule). We ignore
    // the dropped hour here — "move it to today" means "next real slot today."
    const now = new Date()
    const droppedOnToday =
      data.date.getFullYear() === now.getFullYear() &&
      data.date.getMonth() === now.getMonth() &&
      data.date.getDate() === now.getDate()

    if (droppedOnToday) {
      router.patch(
        `/pieces/${slot.piece_id}/slot`,
        {
          slot: {
            fill_today: true,
            manual_publish: slot.manual_publish,
            use_platform_scheduling: slot.use_platform_scheduling,
          },
          return_to: returnTo,
        },
        { preserveScroll: true },
      )
      return
    }

    // Preserve the slot's original minute when dragging into a new cell.
    const oldPublish = new Date(slot.publish_at)
    const newPublish = new Date(oldPublish)
    newPublish.setFullYear(data.date.getFullYear(), data.date.getMonth(), data.date.getDate())
    newPublish.setHours(data.hour, newPublish.getMinutes(), 0, 0)

    // For platform-scheduled slots, shift upload_at by the same delta so
    // the upload→publish offset (the channel's lead time, or whatever the
    // user picked) is preserved on the move.
    const body: {
      slot: {
        publish_at: string
        upload_at?: string
        use_platform_scheduling: boolean
        manual_publish: boolean
      }
    } = {
      slot: {
        publish_at: newPublish.toISOString(),
        manual_publish: slot.manual_publish,
        use_platform_scheduling: slot.use_platform_scheduling,
      },
    }

    if (slot.use_platform_scheduling && slot.upload_at) {
      const deltaMs = newPublish.getTime() - oldPublish.getTime()
      const newUpload = new Date(new Date(slot.upload_at).getTime() + deltaMs)
      body.slot.upload_at = newUpload.toISOString()
    }

    router.patch(`/pieces/${slot.piece_id}/slot`, { ...body, return_to: returnTo }, { preserveScroll: true })
  }

  const navAnchor = (delta: number) => {
    const d = new Date(`${anchor}T00:00:00`)
    if (view === "week") d.setDate(d.getDate() + 7 * delta)
    else d.setMonth(d.getMonth() + delta)
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
    router.get("/calendar", { view, start: iso })
  }

  const setView = (next: CalendarView) => router.get("/calendar", { view: next, start: anchor })

  // Remember the last view per-browser. On a bare `/calendar` visit (no
  // explicit `view` in the URL), restore the saved view; otherwise persist
  // whatever view is active so the next bare visit lands back on it.
  useEffect(() => {
    if (typeof window === "undefined") return
    const hasExplicitView = new URLSearchParams(window.location.search).has("view")
    let saved: string | null = null
    try {
      saved = window.localStorage.getItem(CALENDAR_VIEW_STORAGE_KEY)
    } catch {
      saved = null
    }

    if (!hasExplicitView && saved && saved !== view && isCalendarView(saved)) {
      router.get("/calendar", { view: saved }, { replace: true })
      return
    }

    try {
      window.localStorage.setItem(CALENDAR_VIEW_STORAGE_KEY, view)
    } catch {
      // localStorage unavailable (private mode / blocked) — non-fatal.
    }
  }, [view])

  const headerLabel = useMemo(() => {
    const d = new Date(`${anchor}T00:00:00`)
    if (view === "month") {
      return d.toLocaleDateString(undefined, { month: "long", year: "numeric" })
    }
    const weekStart = new Date(d)
    weekStart.setDate(d.getDate() - d.getDay())
    const weekEnd = new Date(weekStart)
    weekEnd.setDate(weekStart.getDate() + 6)
    return `${weekStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${weekEnd.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`
  }, [anchor, view])

  return (
    <>
      <Head title="Calendar">
        <meta name="description" content="See every scheduled piece across every channel on a unified calendar." />
        <meta property="og:title" content="Calendar" />
        <meta property="og:description" content="See every scheduled piece across every channel on a unified calendar." />
      </Head>
      <AppShell wide="wider">
        <PageHeader
          title="Calendar"
          description="Every scheduled piece across every channel."
          actions={
            <div className="flex items-center gap-2">
              <AddBlockModal
                channels={channels}
                trigger={
                  <Button variant="ghost">
                    <Ban className="h-4 w-4" /> Block time…
                  </Button>
                }
              />
              <BulkPushBackModal
                channels={channels}
                trigger={<Button variant="ghost">Bulk push back…</Button>}
              />
            </div>
          }
        />

        {errorMessage && errorMessage !== dismissedError && (
          <div className="mt-4 flex items-start gap-2 rounded-md border border-danger-display/40 bg-danger-display/10 px-3 py-2 text-sm text-danger-display">
            <span className="min-w-0 flex-1">{errorMessage}</span>
            <button
              type="button"
              onClick={() => setDismissedError(errorMessage)}
              aria-label="Dismiss"
              className="shrink-0 rounded p-0.5 hover:bg-danger-display/10"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="mt-6">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              {view === "list" ? (
                <span className="text-base font-medium text-ink-display">Upcoming</span>
              ) : (
                <>
                  <Button variant="secondary" size="icon" onClick={() => navAnchor(-1)} aria-label="Previous">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button variant="secondary" size="icon" onClick={() => navAnchor(1)} aria-label="Next">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button variant="secondary" onClick={() => router.get("/calendar", { view })}>
                    <CalendarDays className="h-4 w-4" /> Today
                  </Button>
                  <span className="ml-2 text-base font-medium text-ink-display">{headerLabel}</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-3">
              <TagFilter
                tags={tags}
                selected={selectedTagId}
                onSelect={setSelectedTagId}
              />
              <ChannelFilters
                channels={channels}
                visible={visibleChannels}
                onToggle={toggleChannel}
                onSetAll={setAllChannels}
              />
              <ViewToggle view={view} onChange={setView} />
            </div>
          </div>

          <PiecePreviewProvider>
          <DndContext sensors={sensors} onDragEnd={onDragEnd}>
            {view === "week" && (
              <CalendarWeekView
                anchor={anchor}
                slots={visibleSlots}
                channels={channels}
                holidays={holidays}
                templateSlots={template_slots}
                blocks={blocks}
                visibleChannels={visibleChannels}
              />
            )}
            {view === "month" && (
              <CalendarMonthView
                anchor={anchor}
                slots={visibleSlots}
                channels={channels}
                holidays={holidays}
                blocks={blocks}
                visibleChannels={visibleChannels}
                onNavigateMonth={navAnchor}
              />
            )}
            {view === "list" && (
              <CalendarListView
                anchor={anchor}
                slots={visibleSlots}
                channels={channels}
                holidays={holidays}
                templateSlots={template_slots}
                blocks={blocks}
                visibleChannels={visibleChannels}
              />
            )}
          </DndContext>
          </PiecePreviewProvider>
        </div>
      </AppShell>
    </>
  )
}

// Swallow the single click event that follows a drag release. Registered in
// the capture phase on `document` so it runs before React's root listeners
// (stopPropagation keeps the card's own onClick from firing) and before the
// anchor's default navigation (preventDefault). Self-removes after the click,
// or after a short window if no click ever arrives (e.g. the pointer was
// released outside the viewport).
function suppressTrailingClick() {
  const handler = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    remove()
  }
  const remove = () => {
    document.removeEventListener("click", handler, true)
    window.clearTimeout(timer)
  }
  document.addEventListener("click", handler, true)
  const timer = window.setTimeout(remove, 500)
}

const VIEW_OPTIONS: { value: CalendarView; label: string; icon: LucideIcon }[] = [
  { value: "week", label: "Week", icon: Rows3 },
  { value: "month", label: "Month", icon: LayoutGrid },
  { value: "list", label: "List", icon: List },
]

function ViewToggle({ view, onChange }: { view: CalendarView; onChange: (v: CalendarView) => void }) {
  return (
    <div className="inline-flex h-10 rounded-md border border-hairline bg-surface">
      {VIEW_OPTIONS.map((opt, i) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          aria-label={opt.label}
          className={cn(
            "inline-flex items-center gap-2 px-3 text-sm font-medium transition-colors sm:px-4",
            i === 0 && "rounded-l-md",
            i === VIEW_OPTIONS.length - 1 && "rounded-r-md",
            i > 0 && "border-l border-hairline",
            view === opt.value ? "bg-accent-faded text-ink-display" : "text-ink-muted hover:bg-hairline/40"
          )}
        >
          <opt.icon className="h-4 w-4" />
          <span className="hidden sm:inline">{opt.label}</span>
        </button>
      ))}
    </div>
  )
}
